import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { TrendingUp, RefreshCw, AlertTriangle, X } from "lucide-react";
import * as api from "../lib/api";
import { PRIMARY, StatBar, OpportunitySummaryBar, usePersistentState } from "../components/search-performance/shared";
import PagesTab from "../components/search-performance/PagesTab";
import QueriesTab from "../components/search-performance/QueriesTab";
import TrendsTab from "../components/search-performance/TrendsTab";
import ActionQueueTab from "../components/search-performance/ActionQueueTab";
import TopicClustersTab from "../components/search-performance/TopicClustersTab";

const PERIOD_OPTIONS = [
  { value: 7,   label: "7 days" },
  { value: 28,  label: "28 days" },
  { value: 90,  label: "3 months" },
  { value: 180, label: "6 months" },
  { value: 365, label: "12 months" },
  { value: 500, label: "16 months" },
];

export default function SearchPerformancePage() {
  const [tab, setTab] = usePersistentState("tab", "queries");
  const [searchParams, setSearchParams] = useSearchParams();
  const [totals, setTotals] = useState(null);
  const [totalsLoading, setTotalsLoading] = useState(true);
  const [oppSummary, setOppSummary] = useState(null);
  const [oppSummaryLoading, setOppSummaryLoading] = useState(true);
  // Only updated after a refresh succeeds, so it always names the period the data holds.
  const [currentDaysBack, setCurrentDaysBack] = usePersistentState("daysBack", 28);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const pollRef = useRef(null);

  const loadTotals = useCallback(async () => {
    setTotalsLoading(true);
    try { setTotals(await api.getGscSiteTotal()); } catch {}
    setTotalsLoading(false);
  }, []);

  const loadOppSummary = useCallback(async () => {
    setOppSummaryLoading(true);
    try { setOppSummary(await api.getOpportunitiesSummary()); } catch {}
    setOppSummaryLoading(false);
  }, []);

  // Deep links (e.g. the old /dashboard/clusters route) pick a tab via ?tab=.
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) {
      setTab(t);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, setTab]);

  // Topic Clusters → Queries: preset the Queries tab's topic filter (its persisted
  // state is read on mount), then switch. null leaves the current filter alone.
  const openQueries = (clusterId) => {
    if (clusterId != null) {
      try { localStorage.setItem("sp:queries.topicFilter", JSON.stringify(clusterId)); } catch {}
    }
    setTab("queries");
  };

  useEffect(() => { loadTotals(); }, [loadTotals]);
  useEffect(() => { loadOppSummary(); }, [loadOppSummary, refreshKey]);

  const handleRefresh = async (daysBack) => {
    setRefreshing(true);
    setRefreshError("");
    try {
      await api.refreshGsc(daysBack);
      // Poll until done
      pollRef.current = setInterval(async () => {
        try {
          const st = await api.getGscRefreshStatus();
          if (st.status !== "running") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setRefreshing(false);
            if (st.status === "failed") setRefreshError(st.message || st.error || "Refresh failed.");
            setCurrentDaysBack(daysBack);
            setRefreshKey(k => k + 1);
            await loadTotals();
          }
        } catch {
          clearInterval(pollRef.current); pollRef.current = null; setRefreshing(false);
          setRefreshError("Lost contact with the server while refreshing — check backend logs.");
        }
      }, 2000);
    } catch (e) {
      setRefreshing(false);
      setRefreshError(e?.response?.data?.detail || e?.message || "Refresh failed to start.");
    }
  };

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <TrendingUp size={16} strokeWidth={2} className="text-zinc-900" />
          <div>
            <h1 className="text-base font-bold text-zinc-900">Search Performance</h1>
            <p className="text-[11px] text-zinc-400 mt-0.5">
              GSC clicks, impressions, CTR and position — last {currentDaysBack} days
            </p>
          </div>
        </div>

        {/* Period picker + refresh */}
        <div className="flex items-center gap-2">
          <div className="flex gap-0.5 bg-zinc-100 rounded-lg p-0.5">
            {PERIOD_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                disabled={refreshing || analyzing}
                title={analyzing ? "Wait for Run Analysis to finish" : undefined}
                // The active period is already loaded — re-fetching it is the Refresh button's job.
                onClick={() => value !== currentDaysBack && handleRefresh(value)}
                className="px-2.5 py-1 text-[10px] font-bold rounded-md transition-colors whitespace-nowrap disabled:opacity-40"
                style={currentDaysBack === value
                  ? { backgroundColor: PRIMARY, color: "#fff" }
                  : { backgroundColor: "transparent", color: "#71717a" }}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            onClick={() => handleRefresh(currentDaysBack)}
            disabled={refreshing || analyzing}
            title={analyzing ? "Wait for Run Analysis to finish" : undefined}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold text-white rounded-lg disabled:opacity-40 transition-colors"
            style={{ backgroundColor: PRIMARY }}
          >
            <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {refreshError && (
        <div className="flex items-center gap-2 px-4 py-2.5 mb-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600">
          <AlertTriangle size={13} className="flex-shrink-0" />
          {refreshError}
          <button onClick={() => setRefreshError("")} className="ml-auto text-red-300 hover:text-red-600"><X size={12} /></button>
        </div>
      )}

      <div className="space-y-2 mb-8">
        <StatBar totals={totals} loading={totalsLoading} daysBack={currentDaysBack} />
        <OpportunitySummaryBar summary={oppSummary} loading={oppSummaryLoading} />
      </div>

      {/* Tabs */}
      <div className="flex gap-0.5 mb-5 bg-zinc-100 rounded-lg p-0.5 w-fit">
        {[
          { key: "queries", label: "Queries" },
          { key: "topics",  label: "Topic Clusters" },
          { key: "queue",   label: "Action Queue" },
          { key: "pages",   label: "Pages" },
          { key: "trends",  label: "Trends" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className="px-3 py-1 text-xs font-bold rounded-md transition-colors"
            style={tab === key
              ? { backgroundColor: "#fff", color: "#18181b", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }
              : { backgroundColor: "transparent", color: "#71717a" }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "pages"   && <PagesTab key={`pages-${refreshKey}`} onDaysBackDetected={setCurrentDaysBack} />}
      {tab === "queries" && <QueriesTab key={`queries-${refreshKey}`} daysBack={currentDaysBack} refreshing={refreshing} onAnalyzingChange={setAnalyzing} />}
      {tab === "queue"   && <ActionQueueTab key={`queue-${refreshKey}`} />}
      {tab === "topics"  && <TopicClustersTab key={`topics-${refreshKey}`} onOpenQueries={openQueries} />}
      {tab === "trends"  && <TrendsTab key={`trends-${refreshKey}`} daysBack={currentDaysBack} />}
    </div>
  );
}
